import { Component, OnInit, OnChanges, SimpleChanges, Input, Output, EventEmitter } from '@angular/core';
import { Router } from '@angular/router';
import { UserService } from '../../../../../auth/user.service';
import { SiteDashboardService } from '../../../site-dashboard.service';
import { PlinksDashboardService } from '../plinks-dashboard.service';
import { DevicesDashboardService } from '../../../devices/devices-dashboard.service';
import { TranslateService } from '@ngx-translate/core';
import { HttpClient } from '@angular/common/http';
import * as moment from 'moment';
import { CommonUtil } from '../../../../../shared/services/utility/common.service';

@Component({
	selector: 'app-plink-rt-records',
	templateUrl: './plink-rt-records.component.html',
	styleUrls: ['./plink-rt-records.component.css']
})
export class PlinkRtRecordsComponent implements OnInit, OnChanges {

	@Input() device: any = {};
	@Input() deviceType: string = 'plink';
	@Input() date: {
		fromDate: any,
		toDate: any
	} = {
		fromDate: null,
		toDate: null
	};

	@Output() updateAppearanceElementsFlags = new EventEmitter<any>(true);

	plinkID: number = 0;
	PlinkData: any = {};
	currentUser: any = {};
	prevToDate: any;
	prevFromDate: any;
	intervalID: any;

	// Grid configurations
	RTRecordsGridColumns = [];
	RTRecordsGridData = [];

	// Energy format tracking
	energyAnalysis: any = null;
	currentEnergyFormat: string = 'unknown';

	constructor(
		private router: Router,
		private userService: UserService,
		private siteDashboardService: SiteDashboardService,
		private plinksDashboard: PlinksDashboardService,
		private devicesDashboard: DevicesDashboardService,
		private translateService: TranslateService,
		private http: HttpClient,
		private commonUtil: CommonUtil
	) { }
	settings: any = {
		height: "500"
	}

	ngOnInit() {
		this.currentUser = this.userService.getCurrentUser();
		this.siteDashboardService.setActiveSubTab('plink-rt-records');

		// Initialize grid columns
		this.initializeGridColumns();

		// Set up appearance flags for the select-plink component
		this.updateAppearanceElementsFlags.emit({
			addNoteEnabled: false,
			pageHasFilterByDate: true,
			pageHasBattviewTags: true,
			hasResetPLC: false,
			pageHasManageBtn: true,
			pageHasTimesInfo: true,
		});

		// Initialize data structure
		this.initializePlinkData();

		// Get initial data
		this.getEventsData();

		// Set up auto-refresh interval
		this.intervalID = setInterval(() => {
			if(this.plinkID)
				this.fetchPlinkData('MostRecent');
		}, 60000); // Send ajax call every 1 minute
	}

	ngOnChanges(changes: SimpleChanges) {
		// Trigger API call when either device or date changes
		if ((changes['device'] && changes['device'].currentValue) ||
		    (changes['date'] && changes['date'].currentValue)) {
			this.getEventsData();
		}
	}

	ngOnDestroy() {
		if(this.intervalID) {
			clearInterval(this.intervalID);
		}
	}

	initializeGridColumns(energyFormat: string = 'unknown') {
		// Base RT Records grid columns (common to all formats)
		const baseColumns = [
			{headerName: 'ID', field: 'id', flex: 1},
			{headerName: 'Timestamp', field: 'timestampFormattedVal', flex: 1},
			{headerName: 'Total Active Energy KW', field: 'totalActiveEnergyFormatted', flex: 1},
			{headerName: 'Total Billable Energy KWh', field: 'totalBillableEnergyFormatted', flex: 1},
			{headerName: 'Temperature', field: 'temperature', flex: 1},
			{headerName: 'Zone', field: 'zoneID', flex: 1},
			// {headerName: 'RSE ID', field: 'rseID', width: 100},
			// {headerName: 'RSE SN', field: 'rseSN', width: 100},
			// {headerName: 'Network ID', field: 'networkID', width: 100},
			// {headerName: 'Network SN', field: 'networkSN', width: 100}
		];

		// Energy columns based on format
		let energyColumns = [];
		
		// if (energyFormat === 'new' || energyFormat === 'both') {
		// 	// New format: Total kWh
		// 	energyColumns.push({headerName: 'Total kWh', field: 'totalKwh', width: 120, type: 'number'});
		// }
		
		// if (energyFormat === 'legacy' || energyFormat === 'both' || energyFormat === 'unknown') {
		// 	// Legacy format: Phase A/B/C
		// 	energyColumns.push(
		// 		{headerName: 'Phase A kWh', field: 'phaseAkWh', width: 120, type: 'number'},
		// 		{headerName: 'Phase B kWh', field: 'phaseBkWh', width: 120, type: 'number'},
		// 		{headerName: 'Phase C kWh', field: 'phaseCkWh', width: 120, type: 'number'}
		// 	);
		// }

		// // Additional columns
		// const additionalColumns = [
		// 	{headerName: 'Temperature', field: 'temperature', width: 120, type: 'number'},
		// 	{headerName: 'Current', field: 'current', width: 100, type: 'number'},
		// 	{headerName: 'Voltage', field: 'voltage', width: 100, type: 'number'},
		// 	{headerName: 'Event Type', field: 'eventType', width: 120},
		// 	{headerName: 'Event Start Time', field: 'eventStartTimeFormatted', width: 180},
		// 	{headerName: 'LED Status', field: 'ledStatus', width: 120},
		// 	{headerName: 'Connect Time', field: 'totalConnectTime', width: 130, type: 'number'},
		// 	{headerName: 'ActView Enabled', field: 'actViewEnabled', width: 140, type: 'number'}
		// ];

		// Combine all columns
		this.RTRecordsGridColumns = [...baseColumns];
		this.currentEnergyFormat = energyFormat;
	}

	initializePlinkData() {
		this.PlinkData = {
			fromDate: 0,
			toDate: 0,
			fromDateFmt: null,
			toDateFmt: null,
			lastRTRecordID: 0,
			rtRecordsMinDate: 0
		};

		this.RTRecordsGridData = [];
	}

	onDeviceChanged(device) {
		this.device = device;
		this.getEventsData();
	}

	onDatesUpdated(date) {
		this.date = date;
		this.getEventsData();
	}

	getEventsData() {
		// Check for device ID in both possible property names and handle string/number conversion
		const rawDeviceId = this.device?.deviceid || this.device?.id;
		const deviceId = rawDeviceId ? parseInt(rawDeviceId.toString(), 10) : null;

		if (!deviceId) {
			console.log('No device ID available for RT Records');
			return;
		}

		// Reset grid data
		this.RTRecordsGridData = [];

		this.plinkID = deviceId;

		// Initialize data if needed
		if (!this.PlinkData || Object.keys(this.PlinkData).length === 0) {
			this.initializePlinkData();
		}

		// Set up date range
		this.filterPlinkData();

		// Fetch data
		this.fetchPlinkData('DateRange');
	}

	filterPlinkData() {
		if(!this.device || !this.device.deviceid) {
			return;
		}

		// Use date range from input or default to last 7 days
		let fromDate, toDate;
		
		if (this.date && this.date.fromDate && this.date.toDate) {
			fromDate = new Date(this.date.fromDate);
			toDate = new Date(this.date.toDate);
			toDate.setHours(23,59,59,999); // Set to midnight 
		} else {
			// Default to last 7 days
			toDate = new Date();
			fromDate = new Date();
			fromDate.setDate(fromDate.getDate() - 7);
		}

		this.PlinkData.fromDate = Math.floor(fromDate.getTime() / 1000);
		this.PlinkData.toDate = Math.floor(toDate.getTime() / 1000);
		this.PlinkData.fromDateFmt = fromDate;
		this.PlinkData.toDateFmt = toDate;
	}

	fetchPlinkData(from) {
		if(!this.plinkID) {
			return;
		}

		var fromDate = this.PlinkData.fromDate;
		var plinkID = this.plinkID;
		var toDate = 0;
		var lastID = 0;

		if(from === 'DateRange') {
			// For date range queries, use the actual date range
			toDate = this.PlinkData.toDate;
		} else {
			// For 'MostRecent' queries, use the min date and last ID for pagination
			toDate = this.PlinkData.rtRecordsMinDate;
			lastID = this.PlinkData.lastRTRecordID;
		}

		var payLoadObject = {
			'plinkID': plinkID,
			'type': 'rt-records',
			'fromDate': fromDate,
			'toDate': toDate,
			'from': from,
			'lastID': lastID
		};

		this.plinksDashboard.fetchPlinkData(payLoadObject).subscribe(
			(result: any) => {
				// Handle new response format with energy analysis
				let response, energyAnalysis;

				if (result && result.data && result.energyAnalysis) {
					// New format with energy analysis
					response = result.data;
					energyAnalysis = result.energyAnalysis;
				} else {
					// Legacy format or direct data
					response = result.data || result;
					energyAnalysis = null;
				}

				if(response && response.length > 0) {
					// Update energy analysis if available
					if (energyAnalysis) {
						this.energyAnalysis = energyAnalysis;
						// Update grid columns based on energy format
						const formatForGrid = this.determineGridFormat(energyAnalysis);
						if (formatForGrid !== this.currentEnergyFormat) {
							this.initializeGridColumns(formatForGrid);
						}
					}

					function formatTimestampToYMDHMS(unixSeconds) {
						const date = new Date(unixSeconds * 1000);

						return date.getUTCFullYear() + "-" +
						String(date.getUTCMonth() + 1).padStart(2, '0') + "-" +
						String(date.getUTCDate()).padStart(2, '0') + " " +
						String(date.getUTCHours()).padStart(2, '0') + ":" +
						String(date.getUTCMinutes()).padStart(2, '0') + ":" +
						String(date.getUTCSeconds()).padStart(2, '0');
					}
						
					let formattedRes = response.map((item: any) => {
						item.totalActiveEnergyFormatted = item.totalActiveEnergy.toFixed(6);
						item.totalBillableEnergyFormatted = item.totalBillableEnergy.toFixed(3);

						const timestamp = this.commonUtil.getZoneTimestampFromUTC(item.zoneID, item.originalrecordtime, item.enableDayLightSaving);
						item.timestampFormattedVal = formatTimestampToYMDHMS(timestamp);
						return item;
					});

					this.RTRecordsGridData = [...formattedRes, ...this.RTRecordsGridData];
					if(response.length > 0) {
						this.PlinkData.lastRTRecordID = Math.max(...response.map(r => r.id));
						this.PlinkData.rtRecordsMinDate = Math.min(...response.map(r => new Date(r.timestamp).getTime() / 1000));
					}
				}else{
					if(from !== 'MostRecent'){
						this.RTRecordsGridData = [];
					}
				}
			},
			(error) => {
				console.error('Error fetching RT Records data:', error);
			}
		);
	}

	/**
	 * Determine the appropriate grid format based on energy analysis
	 * @param energyAnalysis - Energy format analysis from backend
	 * @returns string - Format identifier for grid columns
	 */
	determineGridFormat(energyAnalysis: any): string {
		if (!energyAnalysis) return 'unknown';

		if (energyAnalysis.hasLegacy && energyAnalysis.hasNew) {
			return 'both'; // Show all columns when both formats exist
		} else if (energyAnalysis.hasNew) {
			return 'new'; // Show only new format columns
		} else if (energyAnalysis.hasLegacy) {
			return 'legacy'; // Show legacy format columns
		} else {
			return 'unknown'; // Default to showing all columns
		}
	}

	onRTRecordCellClicked(record) {
		// Handle RT record cell click if needed
		console.log('RT Record clicked:', record.node.data);
	}
}
